import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Workflows.
 *
 * Perechile vechi: `workflow_settings`, `workflow_list`, `create_workflow`,
 * `update_workflow`, `remove_workflow`, `edit_workflow_form`,
 * `workflowDetails`, `workflow_steps`, `workflow_new_step`,
 * `new_workflow_step`, `update_workflow_step`, `edit_step_form`,
 * `remove_step`, `change_step_assignment`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface WorkflowRow {
  ID: number
  workflow_name: string
  workflow_description: string
  steps: number
  /** Tipurile de tichet care merg pe fluxul asta. */
  used_by: string[]
}

export interface WorkflowStep {
  ID: number
  workflow_id: number
  step_name: string
  status_from: number
  status_to: number
  st_from_name: string
  st_from_color: string
  st_to_name: string
  st_to_color: string
  assign_group_id: number | null
  assign_group_name: string
  assign_user_id: number | null
  assign_user_name: string
}

export interface StepUser {
  ID: number
  user_name: string
  /** Persoana scrisa pe pas care nu e in grupul lui. */
  outside: boolean
}

export interface StatusOption {
  ID: number
  status_name: string
  status_color?: string
}

const BASE = '/settings/easeedesk/workflow_settings'

export function workflowsShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'workflow_settings'],
    queryFn: () =>
      apiGet<{
        title: string
        moduleUrl: string
        groups: Array<{ ID: number; name: string }>
        statuses: StatusOption[]
      }>(BASE),
  })
}

export function workflowsTableQuery(search: string, start: number, length: number) {
  const query = `draw=1&start=${start}&length=${length}&search[value]=${encodeURIComponent(search)}`
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'workflow_settings', 'table', search, start, length],
    queryFn: () =>
      apiGet<{ draw: number; recordsTotal: number; recordsFiltered: number; data: WorkflowRow[] }>(
        `${BASE}/table?${query}`,
      ),
  })
}

export function workflowStepsQuery(workflow: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'workflow_settings', 'steps', workflow],
    queryFn: () =>
      apiGet<{ steps: WorkflowStep[]; assign_users: Record<string, StepUser[]> }>(`${BASE}/${workflow}/steps`),
    enabled: workflow > 0,
  })
}

export function stepOptionsQuery(workflow: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'workflow_settings', 'step_options', workflow],
    queryFn: () =>
      apiGet<{
        workflow: number
        available_from_status: StatusOption[]
        available_to_status: StatusOption[]
        assign_groups: Array<{ ID: number; name: string }>
      }>(`${BASE}/${workflow}/step_options`),
    enabled: workflow > 0,
  })
}

export function stepQuery(step: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'workflow_settings', 'step', step],
    queryFn: () =>
      apiGet<{
        step: WorkflowStep
        is_first: boolean
        available_from_status: StatusOption[]
        available_to_status: StatusOption[]
        assign_groups: Array<{ ID: number; name: string }>
        assign_users: StepUser[]
      }>(`${BASE}/steps/${step}`),
    enabled: step > 0,
  })
}

export function groupUsersQuery(group: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'workflow_settings', 'group_users', group],
    queryFn: () => apiGet<{ users: StepUser[] }>(`${BASE}/groups/${group}/users`),
    enabled: group > 0,
  })
}

export function createWorkflow(name: string, description: string) {
  return apiPost<{ ok?: boolean; ID?: number }>(BASE, {
    workflow_name: name,
    workflow_description: description,
  })
}

export function updateWorkflow(workflow: number, name: string, description: string) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${workflow}`, {
    workflow_name: name,
    workflow_description: description,
  })
}

export function removeWorkflow(workflow: number) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${workflow}/remove`, {})
}

export interface StepForm {
  step_name: string
  step_from: number
  step_to: number
  assign_group_id: number
  assign_user_id: number
}

export function createStep(workflow: number, form: StepForm) {
  return apiPost<{ ok?: boolean; ID?: number }>(`${BASE}/${workflow}/steps`, { ...form })
}

export function updateStep(step: number, form: StepForm) {
  return apiPost<{ ok?: boolean }>(`${BASE}/steps/${step}`, { ...form })
}

export function removeStep(step: number) {
  return apiPost<{ ok?: boolean }>(`${BASE}/steps/${step}/remove`, {})
}

/** `change_step_assignment`: doar cui preda pasul, de pe randul lui. */
export function changeStepAssignment(step: number, ticketType: number, group: number, person: number) {
  return apiPost<{ ok?: boolean }>(`${BASE}/steps/${step}/assignment`, {
    ticket_type: ticketType,
    assign_group_id: group,
    assign_user_id: person,
  })
}
